import { authFetch, getAuthState } from '@addictivegroup/addictive-auth'
import { request, NetworkError, isRetryableStatus } from '@addictivegroup/addictive-offline'
import { withSocket } from './socketId.js'
import { dropStaleCookies } from './staleCookies.js'

export const API_BASE = '/api/v1'

/** A refusal from Core: validation (422), permission (403), conflict (409)… with its code. */
export class ApiError extends Error {
  constructor(status, data) {
    super(data?.message || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = data?.code || null
    this.errors = data?.errors || {}
    this.details = data?.details || null
    this.data = data
  }
}

export { NetworkError }

const sessionLostHandlers = new Set()

/** Called when Core says the session is gone (401/419) while the network works. */
export function onSessionLost(handler) {
  sessionLostHandlers.add(handler)
  return () => sessionLostHandlers.delete(handler)
}

/** Core refused a request for its session (or its XSRF token): whoever listens decides what next. */
export function notifySessionLost() {
  sessionLostHandlers.forEach((h) => h())
}

/** A fresh XSRF token from Core, its cookie set again. */
export async function refreshXsrf() {
  const { config } = getAuthState()
  // A leftover XSRF token of the panel's own name would still be read first (D-247).
  dropStaleCookies(config.backendUrl)
  try {
    await fetch(`${String(config.backendUrl || '').replace(/\/$/, '')}/${String(config.csrfCookiePath || 'sanctum/csrf-cookie').replace(/^\//, '')}`, { credentials: 'include', headers: { Accept: 'application/json' } })
  } catch {
    // no network: whoever asked finds out on the next try
  }
}

const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS']

/**
 * authFetch, a write refused for its XSRF token (419) tried once more with a fresh one: the
 * cookie is the host's, so another app at the same name (a server running several, apart only by
 * port) replaces it with its own; the session itself still lives. Refused again: as Core answered.
 */
export async function coreFetch(path, init = {}) {
  const response = await authFetch(path, init)
  if (response.status !== 419 || SAFE_METHODS.includes(String(init.method || 'GET').toUpperCase())) return response
  await refreshXsrf()
  return authFetch(path, init)
}

/**
 * Query string in the shape the API reads (spatie query builder + Laravel):
 * nested objects become filter[a][b]=…, arrays of scalars are joined with commas
 * (filter[status]=open,won), objects inside arrays keep indexes.
 */
export function queryString(params) {
  const parts = []
  const add = (key, value) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      if (value.length === 0) return
      if (value.every((v) => v === null || typeof v !== 'object')) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`)
      } else {
        value.forEach((v, i) => add(`${key}[${i}]`, v))
      }
      return
    }
    if (typeof value === 'object') {
      for (const [k, v] of Object.entries(value)) add(`${key}[${k}]`, v)
      return
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(typeof value === 'boolean' ? (value ? 'true' : 'false') : value)}`)
  }
  for (const [key, value] of Object.entries(params || {})) {
    if (key === 'filter' && value && typeof value === 'object') {
      for (const [k, v] of Object.entries(value)) {
        // A rules-language condition travels as JSON, not as nested brackets.
        add(`filter[${k}]`, k === 'condition' && v && typeof v === 'object' ? JSON.stringify(v) : v)
      }
    } else {
      add(key, value)
    }
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

/**
 * JSON request to Core. Throws NetworkError when Core cannot be reached (or answers with a
 * status worth retrying), ApiError for any other refusal.
 *
 * @returns {Promise<any>} the parsed body
 */
export async function api(path, { method = 'GET', body, query, headers = {}, timeoutMs = 20000, raw = false } = {}) {
  const url = `${path.startsWith('/api/') ? '' : API_BASE}${path}${queryString(query)}`
  const res = await request(coreFetch, url, { method, body, headers: withSocket(headers), timeoutMs })
  if (res.ok) return raw ? res : res.data
  if (res.status === 401 || res.status === 419) notifySessionLost()
  if (isRetryableStatus(res.status)) throw new NetworkError(`Core answered ${res.status}`)
  throw new ApiError(res.status, res.data)
}

/** Multipart upload (attachments, logos). */
export async function upload(path, file, fields = {}) {
  const form = new FormData()
  form.append('file', file)
  for (const [k, v] of Object.entries(fields)) form.append(k, v)
  let response
  try {
    response = await coreFetch(`${API_BASE}${path}`, { method: 'POST', body: form })
  } catch (error) {
    throw new NetworkError(error?.message || 'Upload failed', error)
  }
  const data = await response.json().catch(() => null)
  if (!response.ok) {
    if (isRetryableStatus(response.status)) throw new NetworkError(`Core answered ${response.status}`)
    throw new ApiError(response.status, data)
  }
  return data
}

/** A file from Core as a blob URL (downloads, previews). */
export async function blobUrl(url) {
  let response
  try {
    response = await authFetch(url.replace(/^https?:\/\/[^/]+/, ''), { method: 'GET', headers: { Accept: '*/*' } })
  } catch (error) {
    throw new NetworkError(error?.message || 'Download failed', error)
  }
  if (!response.ok) throw new ApiError(response.status, await response.json().catch(() => null))
  return URL.createObjectURL(await response.blob())
}

export function unwrap(body) {
  return body && typeof body === 'object' && 'data' in body ? body.data : body
}
